import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate } from 'react-router';
import { isDisplayAccount } from '@/features/auth/display-account';
import { Card, EmptyState, StatTile } from '@/components/ui';
import { useMe, useSession } from '@/features/auth/session-context';
import { useBranch } from '@/features/branch/branch-context';
import { DASHBOARD_TILES } from '@/features/navigation/dashboards';
import { useQueueSummary } from '@/features/queue/api';
import type { QueueSummary } from '@/features/queue/types';
import { PageContainer } from '@/layouts/PageContainer';
import { DoctorDashboard } from './dashboards/DoctorDashboard';
import { FrontDeskDashboard } from './dashboards/FrontDeskDashboard';
import { LabDashboard } from './dashboards/LabDashboard';
import { NurseDashboard } from './dashboards/NurseDashboard';
import {
  DispenserDashboard,
  FinanceDashboard,
  ManagerDashboard,
  SuperAdminDashboard,
  WellbeingDashboard,
} from './dashboards/RoleDashboards';
import styles from './DashboardPage.module.css';
import { useLocumTiles } from './dashboards/locum-tiles';

type TileValue = { value: ReactNode; caption?: ReactNode };

// RL-726..RL-730: every remaining role has its own Admin v2 dashboard.
const ROLE_DASHBOARDS: Partial<Record<string, () => ReactNode>> = {
  dispenser: DispenserDashboard,
  finance: FinanceDashboard,
  mental_health: WellbeingDashboard,
  // RL-1675: Open chats and Waiting for vitals.
  nurse: NurseDashboard,
  clinic_manager: ManagerDashboard,
  super_admin: SuperAdminDashboard,
};

/** Live values for the tiles that have data (queue summary); others stay "—". */
function liveTiles(s: QueueSummary | undefined): Record<string, TileValue> {
  if (!s) return {};
  return {
    'Waiting now': {
      value: s.waiting,
      caption: `${s.inRoom} in room · ${s.done} done`,
    },
    'Booked today': {
      value: s.bookedToday,
      caption: `Arrived so far ${s.arrivedFromBookings}/${s.bookedToday}`,
    },
    'Average wait': {
      value:
        s.averageWaitMinutes === null ? '—' : `${s.averageWaitMinutes} min`,
      caption: 'Arrival to called, today',
    },
    'My patients today': {
      value: s.waiting + s.inRoom,
      caption: `${s.inRoom} in room · ${s.waiting} waiting`,
    },
  };
}

/**
 * Role dashboard: the Admin v2 tile row for the active role. Queue tiles are
 * live (RL-549); the rest fill in as their modules are built.
 */
export function DashboardPage() {
  const me = useMe();
  // RL-1666: a waiting-room display account only has the TV screen.
  if (isDisplayAccount(me.permissions))
    return <Navigate to="/waiting-room" replace />;
  // RL-721 / RL-723 / RL-724: front desk, doctor and lab dashboards are role-specific.
  if (me.activeRole.key === 'front_desk')
    return (
      <PageContainer>
        <FrontDeskDashboard />
      </PageContainer>
    );
  if (me.activeRole.key === 'doctor')
    return (
      <PageContainer>
        <DoctorDashboard />
      </PageContainer>
    );
  if (me.activeRole.key === 'lab')
    return (
      <PageContainer>
        <LabDashboard />
      </PageContainer>
    );
  const Role = ROLE_DASHBOARDS[me.activeRole.key];
  if (Role)
    return (
      <PageContainer>
        <Role />
      </PageContainer>
    );
  return <GenericDashboard />;
}

function GenericDashboard() {
  const { t } = useTranslation();
  const me = useMe();
  const { hasPermission } = useSession();
  const { selected } = useBranch();
  const canSeeQueue =
    hasPermission('queue:read') || hasPermission('my_patients:read');
  const { data: summary } = useQueueSummary(canSeeQueue && !!selected);
  const tiles = DASHBOARD_TILES[me.activeRole.key] ?? [];
  // RL-1808: the locum's own shift, contract and attendance tiles.
  const locum = useLocumTiles(me.activeRole.key === 'locum');
  const live = { ...liveTiles(summary), ...locum };
  const scope = selected?.name ?? t('shell.allBranches');

  return (
    <PageContainer>
      <div className={styles.tiles}>
        {tiles.map((label, i) => (
          <StatTile
            key={label}
            label={label}
            value={live[label]?.value ?? '—'}
            caption={live[label]?.caption}
            highlight={i === 0}
          />
        ))}
      </div>
      <Card title={`Today · ${scope}`}>
        {canSeeQueue ? (
          <EmptyState
            title={hasPermission('queue:read') ? 'Live queue' : 'Your patients'}
            body={
              <Link
                to={hasPermission('queue:read') ? '/queue' : '/my-patients'}
              >
                {hasPermission('queue:read')
                  ? 'Open Queue & arrivals'
                  : 'Open My patients today'}
              </Link>
            }
          />
        ) : (
          <EmptyState
            title={t('common.comingSoon')}
            body={t('empty.dashboardValue')}
          />
        )}
      </Card>
    </PageContainer>
  );
}
